// Modal de criar / editar dados básicos de um role (Name, DisplayName,
// IsDefault). Não trata permissões — para isso, EditRolePermissionsModal.
//
// Padrão de Modal: modal-align-top + fade={false} + div header customizado.

import React, { useEffect, useState, useCallback } from "react";
import {
  Modal,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
} from "reactstrap";

import { createRole, updateRole } from "services/rolesApi";
import { useToast } from "hooks/toast";

const EMPTY = { Name: "", DisplayName: "", IsDefault: false };

export default function EditRoleModal({ isOpen, role, onClose, onSaved }) {
  const isCreate = !role;
  const isStatic = !!(role && role.IsStatic);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const { addToast } = useToast();

  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setForm(
      role
        ? {
            Name: role.Name || "",
            DisplayName: role.DisplayName || "",
            IsDefault: !!role.IsDefault,
          }
        : EMPTY
    );
  }, [isOpen, role]);

  const handleField = useCallback(
    (field) => (e) => {
      const value =
        e && e.target && e.target.type === "checkbox"
          ? e.target.checked
          : e && e.target
          ? e.target.value
          : e;
      setForm((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => {
        if (!prev[field]) return prev;
        const next = { ...prev };
        delete next[field];
        return next;
      });
    },
    []
  );

  const validate = useCallback(() => {
    const e = {};
    if (!form.Name.trim()) e.Name = "Nome obrigatório";
    if (!form.DisplayName.trim()) e.DisplayName = "Nome de exibição obrigatório";
    setErrors(e);
    return Object.keys(e).length === 0;
  }, [form]);

  const handleSave = useCallback(async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        Name: form.Name.trim(),
        DisplayName: form.DisplayName.trim(),
        IsDefault: !!form.IsDefault,
      };
      // Em update de role estático, não enviar Name (backend rejeita também,
      // mas o input fica disabled na UI por consistência)
      if (!isCreate && isStatic) delete payload.Name;
      const saved = isCreate
        ? await createRole(payload)
        : await updateRole(role.Id, payload);
      addToast({
        place: "tr",
        type: "success",
        title: "Salvo",
        description: isCreate ? "Papel criado" : "Papel atualizado",
        icon: "tim-icons icon-check-2",
      });
      if (onSaved) onSaved(saved);
      onClose();
    } catch (err) {
      console.error("[EditRoleModal] save:", err);
      const status = err.response && err.response.status;
      const data = (err.response && err.response.data) || {};
      const message = data.error || "Erro ao salvar papel";
      if ((status === 400 || status === 409) && data.field) {
        setErrors((prev) => ({ ...prev, [data.field]: message }));
      }
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro",
        description: message,
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setSaving(false);
    }
  }, [validate, form, isCreate, isStatic, role, addToast, onSaved, onClose]);

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      modalClassName="modal-align-top"
      fade={false}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "#141b2e",
          borderBottom: "1px solid #2b3553",
          padding: "16px 20px",
        }}
      >
        <span style={{ color: "#f5f7fb", fontWeight: 600, fontSize: 15 }}>
          {isCreate ? "Novo papel" : `Editar — ${role.DisplayName || role.Name}`}
        </span>
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            color: "#f5f7fb",
            fontSize: 22,
            lineHeight: 1,
            cursor: "pointer",
            padding: "0 4px",
          }}
          aria-label="Fechar"
        >
          ×
        </button>
      </div>

      <ModalBody>
        {isStatic && (
          <div
            style={{
              background: "rgba(255,193,7,0.08)",
              borderLeft: "3px solid #ffc107",
              padding: "10px 14px",
              color: "#8fa3c8",
              fontSize: 12,
              marginBottom: 16,
            }}
          >
            Este é um papel <strong>estático</strong> (definido pelo sistema).
            O Name não pode ser alterado e o papel não pode ser excluído.
          </div>
        )}

        <FormGroup>
          <Label>Nome (interno) *</Label>
          <Input
            value={form.Name}
            onChange={handleField("Name")}
            invalid={!!errors.Name}
            disabled={isStatic && !isCreate}
            placeholder="ex: Operador"
          />
          {errors.Name && (
            <div style={{ color: "#ec250d", fontSize: 12, marginTop: 4 }}>
              {errors.Name}
            </div>
          )}
        </FormGroup>

        <FormGroup>
          <Label>Nome de exibição *</Label>
          <Input
            value={form.DisplayName}
            onChange={handleField("DisplayName")}
            invalid={!!errors.DisplayName}
            placeholder="ex: Operador Logístico"
          />
          {errors.DisplayName && (
            <div style={{ color: "#ec250d", fontSize: 12, marginTop: 4 }}>
              {errors.DisplayName}
            </div>
          )}
        </FormGroup>

        <FormGroup check style={{ marginTop: 12 }}>
          <Label check>
            <Input
              type="checkbox"
              checked={!!form.IsDefault}
              onChange={handleField("IsDefault")}
            />
            <span className="form-check-sign" />
            Atribuir automaticamente a novos usuários
          </Label>
        </FormGroup>
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button color="success" onClick={handleSave} disabled={saving}>
          {saving ? "Salvando…" : isCreate ? "Criar" : "Salvar"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
